export const meta = {
  id: '24-body-and-adverbs',
  order: 24,
  titleFi: 'Ihmiskeho ja adverbit',
  titleEn: 'The human body and adverbs',
  duolingoUnit: ['duo-28-the-human-body', 'duo-29-adverbs'],
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: [
    'ondos',
    'pamagon',
    'bartos',
    'ōdragon',
    'ondoso',
    'prūmia',
    'laes',
    'vilagon',
    'viōragon',
    'inkon',
    'ñōghe',
    'deks',
    'kris',
    'mēny',
    'āpsos',
    'eleks',
    'grevion',
    'iemny',
    'lurvon',
    'naejītsos',
    'ñōghybon',
    'ohīlion',
    'ōghar',
    'ēlī',
    'lyka',
    '-irī',
    'lykirī',
    '-ī',
    'aderī',
    'arlie',
    'arlī',
    'ours:nālitse',
    'ours:ondenka', 'ours:purtarys', 'ours:vūjimio', 'ours:geptīha',
    'ours:urnēbnon',
    'ours:vādella',
    'ours:martāves',
    'ours:rhoviapos',
    'ours:mībiragon',
    'ours:ospykagon',
    'ours:elillenka',
    'ours:nervi',
    'ours:dēmamio',
    'ours:oksygeni',
  ],
  exampleIds: [
    'ex-4724356fe143',
    'ex-ffc358bcaa07',
    'ex-bb549449df33',
    'ex-f9975926fe3e',
    'ex-2875d47b2a17',
    'ex-569054a838fc',
    'ex-1693d199fc47',
    'got-s4e03-004',
    'ex-ce208431aad7',
    'got-s3e04-004',
    'ex-5b70f2fd7080',
    'ex-bb7af17e944b',
    'ex-23c06d4f0843',
    'ex-8dc90d192274',
    'ex-f3f40cf567d3',
    'hotd-s3e07-001',
    'got-s3e06-008',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#The_human_body',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Passive',
    'https://wiki.languageinvention.com/index.php?title=Ondoso',
    'https://wiki.languageinvention.com/index.php?title=Vi%C5%8Dragon',
    'https://wiki.languageinvention.com/index.php?title=%C5%8Cdragon',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Adverbs',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Adverbs',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Instrumental',
    'https://dedalvs.tumblr.com/post/181114607984/hi-david-this-hv-duolingo-course-is',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['adverbs'],
  grammarAlso: ['possessives', 'word-order'],
  showLines: ['got-s4e03-004', 'got-s3e04-004', 'hotd-s3e07-001', 'got-s3e06-008'],
  pronunciation: { focus: 'palatals', words: ['ñōghe', 'ñōghybon', 'soljanna', 'riña', 'ñuhon'], lines: ['got-s4e03-004', 'got-s3e04-004', 'got-s3e06-008'] },
  letter: {
    fi: 'Kuvaile lohikäärme tai itsesi: silmät, kädet ja miten se liikkuu.',
    en: 'Describe a dragon or yourself: eyes, hands and how it moves.',
  },
  scene: 'hotd-s3e07-1',
}

# Ihmiskeho ja adverbit

Ensin ihmiskeho: ruumiinosien sanat ja se, miten niistä puhutaan omistussanoilla ja sijoilla. Sitten adverbit: aika, paikka ja tapa.

## Ihmiskeho

Ihmiskehon osa on täynnä sanastoa: pää ja silmät, kädet ja jalat, selkä, vyötärö, sydän. Kielioppia on vähemmän, mutta yksi asia on uusi ja tärkeä: miten valyria kertoo, *kenen* ruumiinosasta on kyse, ja missä sijassa ruumiinosa on, kun sillä tehdään jotain.

## Ruumiinosat ryhmittäin

Sanat on helpointa oppia ryhmittäin, ylhäältä alaspäin kuin kuvaa katsoen:

- **pää:** <Hv glossed>bartos</Hv> ’pää’, <Hv glossed>laes</Hv> ’silmä’, <Hv glossed>eleks</Hv> ’korva’, <Hv glossed>naejītsos</Hv> ’otsa’, <Hv glossed>ōghar</Hv> ’hius, hiuskarva’
- **vartalo:** <Hv glossed>mēny</Hv> ’keho, ruumis’, <Hv glossed>prūmia</Hv> ’sydän’, <Hv glossed>iemny</Hv> ’vatsa’, <Hv glossed>inkon</Hv> ’selkä’, <Hv glossed>grevion</Hv> ’vyötärö’
- **raajat:** <Hv glossed>ondos</Hv> ’käsi’, <Hv glossed>ñōghe</Hv> ’käsivarsi’, <Hv glossed>ñōghybon</Hv> ’kyynärpää’, <Hv glossed>kris</Hv> ’jalka, sääri’, <Hv glossed>deks</Hv> ’jalkaterä’, <Hv glossed>lurvon</Hv> ’nilkka’
- **eläimillä ja lohikäärmeillä:** <Hv glossed>āpsos</Hv> ’kuono’, <Hv glossed>ohīlion</Hv> ’petolinnun tai lohikäärmeen kynsi’

<Example id="ex-569054a838fc" />

Moni sana on aurinkosukua ja kuuluu kolmanteen taivutukseen (<Hv l="bartos">bartos</Hv>, <Hv>ondos</Hv>, <Hv>eleks</Hv>, <Hv>deks</Hv>). Ne taipuvat kuten <Hv>raqiros</Hv>, jonka opit edellisellä oppitunnilla: instrumentaali ja komitatiivi ovat kumpikin <Hv l="ondos">ondoso</Hv>.

<Paradigm lemma="ondos" cases="nom,acc,gen,dat,ins" numbers="sg,pl" />

<Note>
Sanassa <Hv glossed>laes</Hv> ’silmä’ on piilokirjain, joka näkyy vasta taivutuksessa: genetiivi on <Hv>laeho</Hv>. Samanlainen on jo tuttu <Hv glossed>jaos</Hv> ’koira’ → <Hv>jaoho</Hv>. Damien Halme (Academy of High Valyrian) opettaa nämä yhdessä, koska h:ta ei näe perusmuodosta.
</Note>

<Pronounce word="ondossa" />

<Pronounce word="naejītsos" />

<Pronounce word="ñōghe" />

Kaksi äännettä vaatii suomalaiselta huomiota. <Hv>ñ</Hv> on liudentunut n, sama kuin espanjan sanassa *señor*, eikä suomen *ng*. <Hv>gh</Hv> taas on suun takaosassa muodostuva soinnillinen hankausäänne, jota suomessa ei ole lainkaan. Painossakin suomalainen voi kompastua: <Hv>ñōghe</Hv> painottuu ensimmäiselle tavulle, mutta <Hv>ondossa</Hv> on on·DOS·sa ja <Hv>naejītsos</Hv> nae·JĪ·tsos.

## Omistussana: saa olla, mutta ei ole pakko

Joissakin kielissä omista ruumiinosista ei sanota ”minun”, koska se on itsestään selvää. Valyriassa omistusadjektiivi on omien ruumiinosien kanssa aivan luonteva:

<Interlinear hv="Ñuh|ī ond|ossa pam|an." gloss="minun-ACC.PL käsi-ACC.PL hieroa-PRS.1SG" fi="Hieron käsiäni." />

Jos omistaja käy ilmi jo jostakin muusta lauseessa, omistussanaa ei kuitenkaan yleensä tarvita:

<Interlinear hv="Bart|os yne ōdr|as." gloss="pää-NOM minä-ACC sattua-PRS.3SG" fi="Päätäni särkee. (Kirjaimellisesti: Pää satuttaa minua.)" />

Verbin <Hv>ōdragon</Hv> kanssa **kipeä ruumiinosa on subjekti** ja **ihminen, johon sattuu, on akkusatiivissa**. Koska <Hv glossed>yne</Hv> ’minua’ on jo lauseessa, on selvää, kenen päästä on kyse. Sanan <Hv glossed>ñuhys</Hv> ’minun’ lisääminen ei olisi väärin, vain epätavallista.

<Example id="ex-4724356fe143" />

Suomesta on tässä yllättävän paljon apua. Suomeksikin voit sanoa pelkästään *päätä särkee*, ja kuulija ymmärtää, että kyse on sinun päästäsi. Ero on rakenteessa: suomessa kipeä paikka on partitiivissa, valyriassa se on nominatiivissa subjektina, ja kärsijä on akkusatiivissa.

Joskus omistussanaa tarvitaan selvyyden vuoksi. Duolingon Tips & Notes antaa parin: <Hv glossed>Ondossa taobe ōdris</Hv> ’pojan käsiä särkee’ – pojan omat kädet, koska omistussanaa ei ole. Jos alkuun lisätään <Hv>zȳhyz</Hv>, lause tarkoittaa, että *jonkun toisen* kädet satuttavat poikaa. Ohjeissa todetaan itse, että tällainen lause on hyvin omituinen, mutta se näyttää periaatteen: ilman omistussanaa ruumiinosa kuuluu sille, johon lause muutenkin viittaa.

## Omalla kädellä vai toisen kädellä

Tämä on oppitunnin varsinainen uusi sääntö. Kun teet jotain **omalla** ruumiinosallasi, se on **instrumentaalissa**. Kun teet jotain **jonkun toisen** ruumiinosalla, se on **datiivissa**. Duolingon ohjeiden esimerkki ja virallinen lause rinnakkain:

<Interlinear hv="Taob|o bart|os ond|oso pampt|an." gloss="poika-GEN pää-ACC käsi-INS hieroa-PRF.1SG" fi="Hieroin pojan päätä kädelläni." />

<Example id="ex-ffc358bcaa07" />

<Compare l="ondos" fi="käde|llä" hv="ond|oso" label="adessiivi ~ instrumentaali (oma käsi)" />

<Compare l="ondos" fi="käde|llä" hv="ond|ot" label="adessiivi ~ datiivi (toisen käsi)" />

Ensimmäisessä lauseessa <Hv l="ondos">ondoso</Hv> on oma käteni. Toisessa <Hv l="ondos">ondot</Hv> on pojan oma käsi: hieroin pojan päätä *hänen* kädellään. Suomessa tätä eroa ei tehdä lainkaan: meillä kumpikin on adessiivissa, ja omistaja ilmaistaan omistusliitteellä. Valyriassa ero on **pakollinen**, ja juuri siksi omistussanaa ei näissä lauseissa yleensä käytetä: sija kertoo jo, kenen käsi on.

Lähin vastine suomessa on instruktiivi, jota käytetään kiteytyneissä ilmauksissa omista ruumiinosista: *omin käsin*, *omin silmin*, *paljain jaloin*. Pidä se mielessä, kun näet muodon <Hv l="ondos">ondoso</Hv>.

## Ruumiinosat muissa rooleissa

Kun joku koskettaa toista, valyria pitää henkilön ja ruumiinosan erillään. Henkilö on akkusatiivissa ja ruumiinosa omassa sijassaan, eikä omistussanaa tarvita:

<Example id="ex-bb549449df33" />

Tämä on suomalaiselle tuttua: *äiti tarttui poikaa korvasta*. Mekin panemme henkilön ja ruumiinosan eri sijoihin, eikä omistajaa tarvitse mainita erikseen.

Vaate on instrumentaalin oppitunnilta tuttuun tapaan instrumentaalissa, ja ruumiinosa, jossa vaatetta pidetään, on akkusatiivissa:

<Example id="ex-f9975926fe3e" />

Verbien <Hv glossed>vilagon</Hv> ’olla, maata, levätä jossakin’ ja <Hv glossed>viōragon</Hv> ’seistä jonkin päällä’ kanssa paikka on datiivissa. Sanakirja huomauttaa, että yksikössä paikka voi olla myös genetiivissä, kun se on suoraan verbin edellä:

<Example id="ex-2875d47b2a17" />

Tässä <Hv fi="selän">inko</Hv> on genetiivi suoraan <Hv>viōragon</Hv>-verbin edellä. <Hv>Viōragon</Hv> on lauseessa <Term id="infinitiivi">infinitiivissä</Term> eli perusmuodossa (infinitiivistä lisää <LessonLink n={27}>oppitunnilla 27</LessonLink>). Genetiivi selittyy verbin alun <Hv>v-</Hv>-etuliitteellä, joka tekee paikasta verbin täydennyksen; sen säännöt tulevat <LessonLink n={33}>oppitunnilla 33</LessonLink>. Nyt riittää, että tunnistat rakenteen.

## Ondoso – käsi, josta tuli sana

Tämän oppitunnin sanoissa on myös <Hv glossed>ondoso</Hv>, joka on muodoltaan sanan <Hv glossed>ondos</Hv> instrumentaali ’kädellä’. Erillisenä sanana se on <Term id="postpositio">postpositio</Term> ’jonkun toimesta, jonkun kädestä’. Sillä merkitään tekijää passiivilauseissa, ja tekijä on sen edellä genetiivissä. Passiivi tulee vastaan myöhemmin; nyt riittää, että tunnistat sanan.

## Harjoituslauseita

<Practice hv="Ondos yne ōdras." fi="Kättäni särkee." />

<Practice hv="Deks riñe ōdras." fi="Tytön jalkaa särkee." />

<Practice hv="Muña riñe ondoso pamptas." fi="Äiti silitti tyttöä kädellään." />

<Practice hv="Azantys geltose bartos jomīsas." fi="Ritarilla on kypärä päässään." />

## Adverbit

Adverbi on sana, joka voi yksinään muodostaa oman lausekkeensa. Valyriassa adverbeja on kolmea lajia: **ajan** adverbit (milloin), **paikan** adverbit (missä, minne) ja **tavan** adverbit (miten). Lajit eroavat sekä siinä, miten ne muodostetaan, että siinä, mihin kohtaan lausetta ne mieluiten sijoittuvat.

Adverbin itsenäisyyden kuulee parhaiten, kun lohikäärmeille puhutaan. Yksi sana riittää:

<Line id="hotd-s3e07-001" />

<Hv glossed>Lykirī</Hv> on ’tyynesti, rauhallisesti’ – tässä käskynä: ”Rauhassa, Caraxes.”

## Aika ja paikka: lauseen alkuun

Ajan ja paikan adverbien mieluisin paikka on **lauseen aivan alku**. Niitä voi siirtää muualle, jos jotain lauseen osaa halutaan korostaa, mutta alun jälkeen luontevin paikka on **juuri verbin edellä**. Duolingon Tips & Notes näyttää saman lauseen neljässä järjestyksessä:

- <Hv glossed>Ēlī vala taobe ūndas.</Hv> – Ensin mies näki pojan.
- <Hv glossed>Vala taobe ēlī ūndas.</Hv> – Mies näki pojan ensin.
- <Hv glossed>Vala ēlī taobe ūndas.</Hv> – Mies näki pojan ensin.
- <Hv glossed>Taobe ēlī vala ūndas.</Hv> – Mies näki pojan ensin.

Sijapäätteet pitävät huolen siitä, kuka näki kenet, joten sanajärjestyksellä voi ilmaista painotusta. Tämä on suomalaiselle kotoisaa: mekin sanomme *ensin mies näki pojan* tai *pojan mies näki ensin*, ja sijat kertovat, kuka teki mitä. Ero on verbissä, joka pysyy valyriassa lopussa.

<Pronounce l="ēlī" word="ēlī" />

<Example id="ex-1693d199fc47" />

Daenerysin puheessa Meereenin muurien edessä <Hv l="ēlī">ēlī</Hv> aloittaa kaiken:

<Line id="got-s4e03-004" />

### Paikan adverbit syntyvät sijamuodoista

Paikan adverbit johdetaan yleensä substantiiveista **datiivin tai lokatiivin** avulla. Sanasta <Hv glossed>inkon</Hv> ’selkä’ tulee <Hv glossed>inkot</Hv> ’taakse, takana’, ja sanasta <Hv glossed>naejon</Hv> ’etupuoli’ tulee <Hv glossed>naejot</Hv> ’eteen, edessä’.

Suomessa sama ilmiö on tuttu. Meidän *eteen*, *edessä* ja *edestä* ovat alun perin saman vartalon sijamuotoja, ja *taakse* on samanlainen vanha sijamuoto. Valyriassa sijamuodon vain näkee selvemmin.

<Compare l="naejot-2" fi="ete|en" hv="naej|ot" label="illatiivi ~ datiivi/lokatiivi" />

<Line id="got-s3e04-004" />

<Example id="ex-ce208431aad7" />

Viimeisessä esimerkissä on kumpaakin lajia: <Hv glossed>zān</Hv> ’eilen’ on perusmuotoinen ajan adverbi, <Hv l="inkot-2">inkot</Hv> sijamuodosta syntynyt paikan adverbi.

Ajan adverbit taas ovat joko perussanoja, kuten <Hv>zān</Hv>, <Hv>sīr</Hv> ja <Hv l="ēlī">ēlī</Hv>, tai ne johdetaan samalla tavalla kuin tavan adverbit, joihin siirrymme seuraavaksi.

## Tapa: -irī ja -ī

Englannissa tavan adverbit muodostetaan päätteellä *-ly*, suomessa päätteellä *-sti*. Valyriassa pääte riippuu adjektiivin luokasta:

- **<Term id="adjektiiviluokka">I-luokka</Term>:** <Hv glossed>lyka</Hv> ’tyyni, hiljainen’ → <Hv>lyk-</Hv> + <Hv>-irī</Hv> → <Hv glossed>lykirī</Hv> ’hiljaa, tyynesti’
- **<Term id="adjektiiviluokka">II-luokka</Term>:** <Hv glossed>adere</Hv> ’nopea’ → <Hv>ader-</Hv> + <Hv>-ī</Hv> → <Hv glossed>aderī</Hv> ’nopeasti’
- **<Term id="adjektiiviluokka">III-luokka</Term>:** <Hv glossed>arlie</Hv> ’uusi’ → <Hv>arl-</Hv> + <Hv>-ī</Hv> → <Hv glossed>arlī</Hv> ’taas, uudelleen’

<Compare l="aderī" fi="nopea|sti" hv="ader|ī" label="-sti ~ -ī (II-luokka)" />

<Compare l="lykirī" fi="rauhallise|sti" hv="lyk|irī" hvFi="rauhallisesti" label="-sti ~ -irī (I-luokka)" />

<Pronounce l="lykirī" word="lykirī" />

<Pronounce l="aderī" word="aderī" />

Pitkä loppu-ī ei vedä painoa puoleensa: ly·KI·rī, a·DE·rī. Suomalainen saattaa kuulla pitkän vokaalin painollisena, mutta paino on toiseksi viimeisellä tavulla.

Viimeinen pari on opettavainen. <Hv glossed>Arlī</Hv> ei tarkoita ’uudella tavalla’ vaan ’taas, jälleen, takaisin’. Suomessa käy täsmälleen samoin: *uudelleen* on johdettu sanasta *uusi*, mutta se tarkoittaa toistoa eikä uutuutta.

<Note>
**Vinkki:** jos adjektiivin vartalo on epäselvä, katso sen maa- ja vesisuvun muotoa. <Hv glossed>Dāez</Hv> ’vapaa’ on maasuvussa <Hv>dāerior</Hv>, joten adverbi on <Hv glossed>dāerī</Hv> ’vapaasti’. Samoin <Hv glossed>sȳz</Hv> ’hyvä’ → <Hv>sȳrior</Hv> → <Hv glossed>sȳrī</Hv> ’hyvin’. Vinkin antoi Damien Halme (Academy of High Valyrian).
</Note>

<Example id="ex-f3f40cf567d3" />

### Tavan adverbin paikka

Tavan adverbin paikka on vapaampi. **Lauseen alussa** adverbi on korostettu. **Juuri verbin edellä** se on hieman tavallisempi, ja **heti subjektin jälkeen** yhtä yleinen. Kolmessa virallisessa lauseessa näkyvät kaikki kolme:

<Example id="ex-5b70f2fd7080" />

<Interlinear hv="Taob|i vokt|ī lyk|irī sōpt|is." gloss="poika-NOM.PL pappi-ACC.PL hiljaa nauraa-PRF.3PL" fi="Pojat nauroivat hiljaa papeille." />

<Example id="ex-bb7af17e944b" />

<Example id="ex-23c06d4f0843" />

Ensimmäisessä <Hv l="lykirī">lykirī</Hv> on juuri verbin edellä, toisessa lauseen alussa korostettuna (*rauhallisesti lordit pyysivät anteeksi*) ja kolmannessa heti subjektin <Hv>Balerion</Hv> jälkeen – ja samalla tietysti verbin edellä, koska objektia ei ole.

## Yksi sana, kaksi merkitystä

Joidenkin adverbien merkitys riippuu siitä, käytetäänkö niitä tavan vai ajan adverbina. <Hv glossed>Aderī</Hv> on joko ’nopeasti’ tai ’pian’. Yllä Balerion kaarsi *nopeasti*; seuraavassa sama sana ilmaisee aikaa:

<Example id="ex-8dc90d192274" />

Asiayhteys ratkaisee. Suomessa *pian* ja *nopeasti* ovat eri sanoja, mutta valyriassa merkitys on pääteltävä tilanteesta.

## Sijamuodot adverbeina

Wikin kielioppisivun mukaan adverbeina käytetään usein myös substantiivien datiivi-, lokatiivi-, instrumentaali- ja komitatiivimuotoja. Instrumentaalista syntyy tavan adverbeja: Thorosin repliikissä <Hv l="aōhoso" glossed>aōhoso</Hv> ja <Hv glossed>ñuhoso</Hv> ovat ’sinun tavallasi’ ja ’minun tavallani’, ja <Hv glossed>Quptenkos Ēngoso</Hv> on kirjaimellisesti ’yhteisen kielen keinoin’.

<Line id="got-s3e06-008" />

<Note>
Thorosin <Hv>ȳdrassis</Hv> on Petersonin mukaan virhe: valyriassa ei puhutella kohteliaasti kolmannessa persoonassa, joten oikein olisi 2. persoona <Hv>ȳdrassia</Hv> (Petersonin Tumblr-vastaus 2018; myös wikin alaviite).
</Note>

## Harjoituslauseita

<Practice hv="Ēlī riña ābre ūndas." fi="Ensin tyttö näki naisen." />

<Practice hv="Vala lykirī ȳdras." fi="Mies puhuu hiljaa." />

<Practice hv="Ābre arlī ūndan." fi="Näin naisen taas." />

<Practice hv="Taoba aderī iprattas." fi="Poika söi nopeasti." />

## Sanat

<Vocab />

<Check />
